import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Avatar,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useTranslation } from '../../../app/LanguageContext';
import { CurrencyTextField, CostField, COMPACT_COST_FIELD_WIDTH, COMPACT_PRICE_FIELD_WIDTH } from '../../core/public';
import PriceWithSimulatorField from '../../../extensions/price-simulator-3d/PriceWithSimulatorField';
import { PRODUCT_DEFAULT_IMAGE } from '../../medias/public/MediaGallery';
import {
  buildVariantTitle,
  buildSkuPriceSimulatorContext,
  countActiveVariants,
  filterVariants,
  groupVariantsByOption,
  isVariantReadOnly,
  partitionVariants,
  VARIANT_EXPAND_ALL_ACTIVE_MAX,
} from './productVariantUtils';
import { mutedSurfaceBg, subtleSurfaceBg } from '../../../theme/surfaceSx';
import VariantListToolbar from './VariantListToolbar';
import LegacyVariantsPanel from './LegacyVariantsPanel';

const VARIANT_ROW_SX = {
  '& > td': {
    verticalAlign: 'top',
    py: 1,
  },
};

export default function ProductVariantsTable({
  options = [],
  variants = [],
  productName = '',
  defaultVariableCosts = [],
  groupByIndex = 0,
  onGroupByChange,
  onVariantClick,
  onVariantPatch,
  onApplySimulatorPrice,
  onEditOptions,
  onFilteredChange,
  onPurgeLegacy,
  purgingLegacy = false,
  legacyCount = 0,
  legacyPreview = [],
}) {
  const { translate } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const skipAutoExpandRef = React.useRef(false);

  const filtered = useMemo(
    () => filterVariants(variants, options, { searchQuery, filters }),
    [variants, options, searchQuery, filters],
  );

  const { editable: editableFiltered } = useMemo(
    () => partitionVariants(filtered, options),
    [filtered, options],
  );

  const { editable: editableAll } = useMemo(
    () => partitionVariants(variants, options),
    [variants, options],
  );

  const legacyPreviewVariants = useMemo(() => {
    if (legacyPreview?.length) return legacyPreview;
    return partitionVariants(variants, options).legacy.slice(0, 20);
  }, [legacyPreview, variants, options]);

  React.useEffect(() => {
    onFilteredChange?.(editableFiltered);
  }, [editableFiltered, onFilteredChange]);

  const { grouped, order } = useMemo(
    () => groupVariantsByOption(editableFiltered, options, groupByIndex),
    [editableFiltered, options, groupByIndex],
  );

  const hasActiveFilters = Object.values(filters).some(Boolean);

  const activeVariantCount = useMemo(() => countActiveVariants(variants), [variants]);

  const getDefaultExpandedGroups = React.useCallback(
    (groupOrder) => {
      if (!groupOrder.length) return new Set();
      if (activeVariantCount < VARIANT_EXPAND_ALL_ACTIVE_MAX) {
        return new Set(groupOrder);
      }
      return new Set([groupOrder[0]]);
    },
    [activeVariantCount],
  );

  React.useEffect(() => {
    skipAutoExpandRef.current = false;
    if (order.length) {
      setExpandedGroups(getDefaultExpandedGroups(order));
    }
  }, [groupByIndex, order.join('|'), activeVariantCount, getDefaultExpandedGroups]);

  React.useEffect(() => {
    if (order.length && expandedGroups.size === 0 && !skipAutoExpandRef.current) {
      setExpandedGroups(getDefaultExpandedGroups(order));
    }
  }, [order, expandedGroups.size, getDefaultExpandedGroups]);

  const allGroupsExpanded =
    order.length > 0 && order.every((groupValue) => expandedGroups.has(groupValue));

  const handleExpandAllGroups = () => {
    skipAutoExpandRef.current = false;
    setExpandedGroups(new Set(order));
  };

  const handleCollapseAllGroups = () => {
    skipAutoExpandRef.current = true;
    setExpandedGroups(new Set());
  };

  const toggleGroup = (groupValue) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupValue)) next.delete(groupValue);
      else next.add(groupValue);
      return next;
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const renderVariantRow = (variant) => {
    const title = buildVariantTitle(variant, options);
    const inactive = variant.status === 'INACTIVE';
    const readOnly = isVariantReadOnly(variant, options);
    const simulatorContext = buildSkuPriceSimulatorContext({
      productName,
      variant,
      options,
      defaultVariableCosts,
      readOnly,
      onApplySimulatedPrice: onApplySimulatorPrice
        ? (payload) => onApplySimulatorPrice(variant.id, payload)
        : undefined,
    });

    return (
      <TableRow
        key={variant.id}
        sx={{
          opacity: inactive ? 0.55 : 1,
          '&:last-child td': { borderBottom: 0 },
          ...VARIANT_ROW_SX,
        }}
      >
        <TableCell padding="checkbox" />
        <TableCell>
          <Avatar
            variant="rounded"
            src={PRODUCT_DEFAULT_IMAGE}
            sx={{ width: 36, height: 36, cursor: 'pointer' }}
            onClick={() => onVariantClick?.(variant)}
          />
        </TableCell>
        <TableCell sx={{ cursor: 'pointer' }} onClick={() => onVariantClick?.(variant)}>
          <Typography variant="body2">
            {title}
            {variant.is_made_to_order && (
              <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.75 }}>
                ({translate('products.made_to_order_label')})
              </Typography>
            )}
            {inactive && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                ({translate('products.variant_status_inactive')})
              </Typography>
            )}
          </Typography>
        </TableCell>
        <TableCell>
          <CostField
            size="small"
            showLabel={false}
            showHelper={false}
            value={variant.cost ?? 0}
            onChange={(value) => onVariantPatch?.(variant.id, { cost: value })}
            sx={{ width: COMPACT_COST_FIELD_WIDTH, mt: 0.25 }}
          />
        </TableCell>
        <TableCell sx={{ minWidth: 300 }}>
          <PriceWithSimulatorField
            size="small"
            value={variant.price ?? 0}
            onChange={(value) => onVariantPatch?.(variant.id, { price: value })}
            readOnly={readOnly}
            showSimulator={!readOnly && Boolean(onApplySimulatorPrice)}
            summaryCompact
            sx={{ width: COMPACT_PRICE_FIELD_WIDTH, mt: 0.25 }}
            summarySx={{ mt: 0.75 }}
            slotId="product.sku.price"
            simulatorContext={simulatorContext}
          />
        </TableCell>
        <TableCell>
          {variant.is_made_to_order ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <TextField
              type="number"
              size="small"
              value={variant.stock ?? 0}
              onChange={(e) => onVariantPatch?.(variant.id, { stock: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, step: 1 }}
              sx={{ width: 72, mt: 0.25 }}
            />
          )}
        </TableCell>
        <TableCell>
          <TextField
            type="number"
            size="small"
            value={variant.manufacturing_days || ''}
            placeholder="—"
            onChange={(e) =>
              onVariantPatch?.(variant.id, {
                manufacturing_days: e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0,
              })
            }
            inputProps={{ min: 0 }}
            sx={{ width: 72, mt: 0.25 }}
          />
        </TableCell>
        <TableCell>
          <FormControlLabel
            sx={{ m: 0, mt: 0.25, alignItems: 'center' }}
            control={
              <Switch
                size="small"
                checked={(variant.status || 'ACTIVE') === 'ACTIVE'}
                onChange={(e) =>
                  onVariantPatch?.(variant.id, { status: e.target.checked ? 'ACTIVE' : 'INACTIVE' })
                }
              />
            }
            label={
              (variant.status || 'ACTIVE') === 'ACTIVE'
                ? translate('products.variant_status_active')
                : translate('products.variant_status_inactive')
            }
          />
        </TableCell>
      </TableRow>
    );
  };

  const showGrouped = options.length > 0 && order.length > 0;
  const canToggleAllGroups = showGrouped && order.length > 1 && !hasActiveFilters && !searchQuery;

  return (
    <Box>
      {options.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {options.map((opt) => (
            <Paper
              key={opt.key || opt.name}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: subtleSurfaceBg,
                gap: 1,
              }}
            >
              <Typography variant="body2">
                <strong>{opt.name}</strong>: {(opt.values || []).join(', ')}
              </Typography>
              {onEditOptions && (
                <Button size="small" onClick={onEditOptions}>
                  {translate('common.edit')}
                </Button>
              )}
            </Paper>
          ))}
        </Box>
      )}

      {variants.length > 0 && options.length > 0 && (
        <VariantListToolbar
          options={options}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={handleFilterChange}
          groupByIndex={groupByIndex}
          onGroupByChange={onGroupByChange}
          filteredCount={editableFiltered.length}
          totalCount={editableAll.length}
          hasActiveFilters={hasActiveFilters}
          showGroupControls={canToggleAllGroups}
          allGroupsExpanded={allGroupsExpanded}
          onExpandAllGroups={handleExpandAllGroups}
          onCollapseAllGroups={handleCollapseAllGroups}
        />
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell width={48} />
              <TableCell>{translate('products.variant')}</TableCell>
              <TableCell>{translate('core.cost.label')}</TableCell>
              <TableCell sx={{ minWidth: 300 }}>{translate('products.price')}</TableCell>
              <TableCell>{translate('products.stock')}</TableCell>
              <TableCell>{translate('products.manufacturing_days')}</TableCell>
              <TableCell>{translate('products.variant_status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!variants.length ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('products.variants_empty')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : showGrouped ? (
              order.map((groupValue) => {
                const items = grouped.get(groupValue) || [];
                const open = expandedGroups.has(groupValue) || hasActiveFilters || Boolean(searchQuery);
                return (
                  <React.Fragment key={groupValue}>
                    <TableRow sx={{ bgcolor: mutedSurfaceBg }}>
                      <TableCell colSpan={8}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                          onClick={() => toggleGroup(groupValue)}
                        >
                          <IconButton size="small">
                            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                          <Typography variant="body2" fontWeight={600}>
                            {groupValue}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {translate('products.variant_group_count').replace('{count}', String(items.length))}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {open && items.map(renderVariantRow)}
                  </React.Fragment>
                );
              })
            ) : (
              editableFiltered.map(renderVariantRow)
            )}
            {variants.length > 0 && editableFiltered.length === 0 && legacyCount === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {translate('products.variants_no_match')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <LegacyVariantsPanel
          variants={legacyPreviewVariants}
          legacyCount={legacyCount}
          options={options}
          onVariantClick={onVariantClick}
          onPurgeUnused={onPurgeLegacy}
          purging={purgingLegacy}
        />
      </TableContainer>
    </Box>
  );
}
